import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);


export const getCurrentTimezone = () => {
  const stored = localStorage.getItem('timezone_preference');
  if (stored && isValidTimezone(stored)) {
    return stored;
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * @param {string} timezone 
 */
export const setTimezonePreference = (timezone) => {
  if (isValidTimezone(timezone)) {
    localStorage.setItem('timezone_preference', timezone);
  }
};


export const getBrowserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * @param {object} project - Project object containing timezone info
 */
export const getProjectTimezone = (project) => {
  return project?.timezone || getBrowserTimezone();
};

/**
 * @param {string} timezone - Timezone string to validate
 */
export const isValidTimezone = (timezone) => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

/**
 * @param {number} unixTimestamp - Unix timestamp in seconds
 * @param {string} targetTimezone - Target timezone
 */
export const convertUnixToTimezone = (unixTimestamp, targetTimezone = null) => {
  if (!unixTimestamp) return null;
  
  const timezone = targetTimezone || getCurrentTimezone();
  
  const date = new Date(unixTimestamp * 1000);
  
  return {
    date: date,
    timezone: timezone,
    format: (formatString) => {
      return dayjs(date).tz(timezone).format(formatString);
    },
    toLocaleString: (options = {}) => {
      return date.toLocaleString('en-US', {
        timeZone: timezone,
        ...options
      });
    }
  };
};

/**
 * @param {number} timestamp - Unix timestamp in seconds
 * @param {string} format - dayjs format string (optional)
 * @param {string} targetTimezone - Target timezone (optional)
 */
export const formatTimestamp = (timestamp, format = 'DD-MMM-YYYY h:mm A', targetTimezone = null) => {
  if (!timestamp) return '';
  
  const timezone = targetTimezone || getCurrentTimezone();
  
  try {
    const date = dayjs.unix(timestamp).tz(timezone);
    return date.format(format);
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return '';
  }
};

/**
 * @param {number} timestamp - Unix timestamp in seconds
 * @param {string} targetTimezone - Target timezone (optional)
 */
export const formatTimestampSeparate = (timestamp, targetTimezone = null) => {
  if (!timestamp) return { date: '', time: '', timezone: '' };
  
  const timezone = targetTimezone || getCurrentTimezone();
  
  try {
    const date = dayjs.unix(timestamp).tz(timezone);
    
    return {
      date: date.format('DD-MMM-YYYY'),
      time: date.format('h:mm A'),
      timezone: timezone
    };
  } catch (error) {
    console.error('Error formatting timestamp separate:', error);
    return { date: '', time: '', timezone: '' };
  }
};

/**
 * @param {number} ts - Unix timestamp in seconds
 * @param {string} targetTimezone - Target timezone (optional)
 */
export const formatTimestampForDisplay = (ts, targetTimezone = null) => {
  if (!ts) return <div>-</div>;
  
  const timezone = targetTimezone || getCurrentTimezone();
  
  try {
    const date = dayjs.unix(ts).tz(timezone);
    
    const dateStr = date.format('DD-MMM-YYYY'); 
    const timeStr = date.format('h:mm A');      

    return (
      <div>
        <div>{dateStr}</div>
        <div>{timeStr}</div>
      </div>
    );
  } catch (error) {
    console.error('Error formatting timestamp for display:', error);
    return <div>Error</div>;
  }
};

/**
 * Convert from timestamp with timezone info
 * @param {number} timestamp - Unix timestamp in seconds
 * @param {string} targetTimezone - Target timezone (optional)
 */
export const convertFromTimestamp = (timestamp, targetTimezone = null) => {
  if (!timestamp) return { date: '', time: '', timezone: '' };
  
  const timezone = targetTimezone || getCurrentTimezone();
  
  try {
    const date = dayjs.unix(timestamp).tz(timezone);
    
    return {
      date: date.format('DD MMM YYYY'),
      time: date.format('hh:mm A'),
      timezone: timezone
    };
  } catch (error) {
    console.error('Error converting from timestamp:', error);
    return { date: '', time: '', timezone: '' };
  }
};

/**
 * @param {number} unixTimestamp - Unix timestamp in seconds
 * @param {string} targetTimezone - Target timezone (optional)
 */
export const formatUnixDate = (unixTimestamp, targetTimezone = null) => {
  if (!unixTimestamp) return { date: '', time: '', timezone: '' };

  const timezone = targetTimezone || getCurrentTimezone();
  
  try {
    const date = dayjs.unix(unixTimestamp).tz(timezone);
    
    return {
      date: date.format('DD-MM-YYYY'),
      time: date.format('HH:mm:ss'),
      timezone: timezone
    };
  } catch (error) {
    console.error('Error formatting unix date:', error);
    return { date: '', time: '', timezone: '' };
  }
};

/**
 * @param {string} targetTimezone - Target timezone (optional)
 */
export const getCurrentTime = (targetTimezone = null) => {
  const timezone = targetTimezone || getCurrentTimezone();
  return dayjs().tz(timezone);
};

/**
 * @param {string} timezone - Timezone string (optional, uses current if not provided)
 */
export const getTimezoneOffset = (timezone = null) => {
  const tz = timezone || getCurrentTimezone();
  return dayjs().tz(tz).format('Z');
};

/**
 * @param {number|string|Date} date - Date to convert
 * @param {string} fromTimezone - Source timezone
 * @param {string} toTimezone - Target timezone
 */
export const convertTimezone = (date, fromTimezone, toTimezone) => {
  return dayjs.tz(date, fromTimezone).tz(toTimezone);
};

/**
 * @param {string} timezone - Timezone string
 */
export const getTimezoneDisplayName = (timezone) => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'long'
    });
    const parts = formatter.formatToParts(now);
    const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value;
    return timeZoneName || timezone;
  } catch {
    return timezone;
  }
};

/**
 * @param {number} timestamp - Unix timestamp to test
 * @param {string} timezone - Timezone to test
 */
export const debugTimezoneConversion = (timestamp, timezone) => {
  
  const nativeDate = new Date(timestamp * 1000);
  const nativeFormatted = nativeDate.toLocaleString('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
  
  const dayjsDate = dayjs.unix(timestamp).tz(timezone);
  const dayjsFormatted = dayjsDate.format('DD-MMM-YYYY h:mm:ss A');
  
  return {
    utc: new Date(timestamp * 1000).toISOString(),
    native: nativeFormatted,
    dayjs: dayjsFormatted,
    timezone: timezone,
    offset: dayjsDate.format('Z')
  };
};

/**
 * @param {string} dateString
 * @param {string} timezone
 */
export const createTimestampFromTimezone = (dateString, timezone) => {
  try {
    const date = dayjs.tz(dateString, timezone);
    return date.unix();
  } catch (error) {
    console.error('Error creating timestamp from timezone:', error);
    return null;
  }
};

const timezoneUtils = {
  getCurrentTimezone,
  setTimezonePreference,
  getBrowserTimezone,
  getProjectTimezone,
  formatTimestamp,
  formatTimestampSeparate,
  formatTimestampForDisplay,
  convertFromTimestamp,
  formatUnixDate,
  getCurrentTime,
  isValidTimezone,
  getTimezoneOffset,
  convertTimezone,
  getTimezoneDisplayName,
  debugTimezoneConversion,
  convertUnixToTimezone,
  createTimestampFromTimezone
};

export default timezoneUtils;
